import { useState } from 'react';

interface DeleteAccountModalProps {
  onClose: () => void;
  onConfirm: (password: string) => Promise<void>;
}

export default function DeleteAccountModal({
  onClose,
  onConfirm,
}: DeleteAccountModalProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await onConfirm(password);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Noget gik galt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      <div className="surface w-full max-w-md p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 id="delete-account-title" className="text-xl font-semibold text-strong">
            Slet konto
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-faint hover:text-strong"
            aria-label="Luk"
          >
            ✕
          </button>
        </div>

        <p className="mt-2 text-sm text-muted">
          Alle profiler knyttet til din konto slettes permanent, og du logges ud.
          Dette kan ikke fortrydes.
        </p>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <label className="block">
            <span className="label">Bekræft med adgangskode</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-input mt-1 w-full rounded-lg bg-white px-3 py-2 text-zinc-900 ring-1 ring-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-zinc-950 dark:text-zinc-100 dark:ring-white/10"
            />
          </label>
          {error && (
            <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn-secondary flex-1 disabled:opacity-50"
            >
              Annuller
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-500 disabled:opacity-50"
            >
              {busy ? 'Sletter…' : 'Slet konto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
